import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X } from "lucide-react";

const REASONS = [
  { value: "resigned", label: "Resignation" },
  { value: "terminated_cause", label: "Termination (for cause)" },
  { value: "terminated_no_cause", label: "Termination (without cause)" },
  { value: "layoff", label: "Layoff / Redundancy" },
  { value: "contract_ended", label: "Contract Ended" },
  { value: "retired", label: "Retirement" },
  { value: "mutual_separation", label: "Mutual Separation" },
  { value: "absconded", label: "Absconded / Job Abandonment" },
  { value: "deceased", label: "Deceased" },
  { value: "other", label: "Other" },
];

export default function DeleteReasonModal({ employee, onConfirm, onCancel, isLoading }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const selected = REASONS.find((r) => r.value === reason);
  const requiresNotes = reason === "other";
  const canConfirm = reason && (!requiresNotes || notes.trim().length > 0);

  const handleConfirm = () => {
    if (!canConfirm) {
      setError(requiresNotes ? "Please describe the reason." : "Please select a reason.");
      return;
    }
    onConfirm({
      reason_code: reason,
      reason_label: selected?.label || "Other",
      notes: notes.trim(),
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md"
      >
        <Card className="border-none shadow-2xl">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Remove Employee</h3>
                  <p className="text-sm text-gray-500">
                    {employee?.first_name} {employee?.last_name}
                  </p>
                </div>
              </div>
              <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-3">
              This will move the employee to former employees and record the separation reason.
            </p>

            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Reason for leaving <span className="text-red-500">*</span>
            </label>
            <select
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError("");
              }}
              className="w-full border border-gray-200 rounded-md p-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a reason...</option>
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>

            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Notes {requiresNotes && <span className="text-red-500">*</span>}
              {!requiresNotes && <span className="text-gray-400">(optional)</span>}
            </label>
            <textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setError("");
              }}
              placeholder={
                requiresNotes
                  ? "Please describe the reason for leaving..."
                  : "Any additional context (optional)..."
              }
              className="w-full border border-gray-200 rounded-md p-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />

            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

            <div className="flex gap-3 justify-end mt-4">
              <Button variant="outline" onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                disabled={isLoading}
                onClick={handleConfirm}
              >
                {isLoading ? "Removing..." : "Remove Employee"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}